// Applies a tour's enterprise branding (validated by the server, see server/src/branding.js) to the
// viewer's design tokens in style.css: colours for light and dark, corner radius, font, shadow, blur.
import { Color } from 'playcanvas';

const COLOR_VARS = {
    background: '--bg', accent: '--accent', accentText: '--accent-ink', surface: '--chip',
    surfaceText: '--chip-ink', overlay: '--pill', overlayText: '--pill-ink', border: '--border'
};
const SHADOWS = {
    none: 'none',
    hairline: '0 1px 2px rgba(0, 0, 0, 0.08)',
    soft: '0 2px 8px rgba(0, 0, 0, 0.12)',
    medium: '0 4px 16px rgba(0, 0, 0, 0.18)'
};
// Defence in depth: the server already validates, but never let anything CSS-breaking through
const safe = v => (typeof v === 'string' && /^[#(),.\w\s%-]+$/.test(v) ? v : null);

// A brand's accent also fills what is chosen (Horama's pearl gradient otherwise); its surfaces and overlays stay as
// they are where the blur behind the glass is switched off (weak graphics)
const ALSO = { accent: '--accent-fill', surface: '--chip-solid', overlay: '--pill-solid' };

function block(colors) {
    return Object.entries(colors || {})
        .map(([k, v]) => (COLOR_VARS[k] && safe(v) ? `${COLOR_VARS[k]}: ${v};${ALSO[k] ? ` ${ALSO[k]}: ${v};` : ''}` : ''))
        .join(' ');
}

/** Parse "#rrggbb" / "#rgb" / "rgb(r,g,b)" into a PlayCanvas Color, or null. */
export function parseColor(v) {
    if (typeof v !== 'string') return null;
    let m = v.match(/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
    if (m) {
        let h = m[1];
        if (h.length <= 4) h = [...h].map(c => c + c).join('');
        return new Color(parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255);
    }
    m = v.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    return m ? new Color(m[1] / 255, m[2] / 255, m[3] / 255) : null;
}

/**
 * @param {object} b - branding
 * @param {import('./ui.js').UI} ui
 * @returns {{branded: boolean, clear: {light: Color|null, dark: Color|null}}}
 */
export function applyBranding(b, ui) {
    if (!b || !Object.keys(b).length) return { branded: false, clear: { light: null, dark: null } };
    // `themed`: values the dark theme of style.css sets as well; they go into the brand's dark rules too, which would
    // otherwise lose to style.css's (a more specific selector)
    const root = [], themed = [];
    if (typeof b.radius === 'number') root.push(`--radius: ${Math.round(b.radius)}px;`);
    if (typeof b.panelRadius === 'number') root.push(`--radius-panel: ${Math.round(b.panelRadius)}px;`);
    if (SHADOWS[b.shadow]) themed.push(`--shadow: ${SHADOWS[b.shadow]};`);
    if (b.blur === false) { root.push('--blur: none;'); themed.push('--sheen: none;'); }
    // The guide drawings take the text colour instead of Horama's ice blue once the brand has an accent of its own
    if (b.light?.accent || b.dark?.accent) themed.push('--hl: currentColor;');
    const font = b.font || {};
    if (font.family && /^[\w -]+$/.test(font.family)) {
        root.push(`--font: "${font.family}", system-ui, -apple-system, "Segoe UI", sans-serif;`);
        if (font.url && 'FontFace' in window) {
            const face = new FontFace(font.family, `url("${encodeURI(font.url)}")`, { weight: '100 900', display: 'swap' });
            face.load().then(f => document.fonts.add(f)).catch(() => console.warn('brand font failed to load'));
        }
    }
    if (typeof font.weight === 'number') root.push(`--font-weight: ${font.weight};`);
    if (typeof font.scale === 'number') root.push(`--font-scale: ${font.scale};`);

    const light = block(b.light), dark = [themed.join(' '), block(b.dark)].filter(Boolean).join(' ');
    const css = [
        `:root { ${root.join(' ')} ${themed.join(' ')} ${light} }`,
        dark && `@media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) { ${dark} } }`,
        dark && `:root[data-theme="dark"] { ${dark} }`,
        typeof font.titleWeight === 'number' && `.title { font-weight: ${font.titleWeight}; }`
    ].filter(Boolean).join('\n');
    const style = document.createElement('style');
    style.id = 'brand';
    style.textContent = css;
    document.head.appendChild(style);

    if (b.logo?.url) ui.setLogo(b.logo.url, b.logo.alt, b.logo.hideTitle);
    return { branded: true, clear: { light: parseColor(b.light?.background), dark: parseColor(b.dark?.background) } };
}
